import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { Province } from '../../types/province';
import { getRaceDisplayName, getPersonalityDisplayName, getRaceUnitName } from '../../constants/game';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { deriveAlerts } from '../../utils/alerts';
import { isEffectBeneficialToOwner } from '../../utils/effectOwnerImpact';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { COLORS } from '../../constants/theme';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { EffectRow } from '../shell/EffectRow';
import { ArmyRow } from '../shell/ArmyRow';
import { AlertRow } from '../shell/AlertRow';
import { StatusBanner } from '../shell/StatusBanner';
import { RaceCrest } from '../shell/RaceCrest';
import { useThroneStatusBanners } from './useStatusBanners';
import { NotificationBellLink } from './NotificationBellLink';
import { IncomingActivitySection } from './IncomingActivitySection';
import {
  summarizeArmyUnits,
  ordinalSuffix,
  describeCombatEvent,
  stripEmbeddedLocation,
  formatCurrentGameDate,
} from './throneFormatting';

const RECENT_ACTIVITY_CAP_MOBILE = 3;

interface MobileThroneShellProps {
  province: Province;
}

export function MobileThroneShell({ province }: MobileThroneShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const { count: unreadCount } = useUnreadCount();
  const { banners, announcement } = useThroneStatusBanners(province);
  const alerts = deriveAlerts(province);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const { metadata, military, population, magic, sync } = province;

  const recentActivity = [...military.recentCombat]
    .sort((a, b) => b.launchTick - a.launchTick)
    .slice(0, RECENT_ACTIVITY_CAP_MOBILE);

  return (
    <div className="space-y-3">
      {/* SF-122: visually-present page <h1> — every GlanceCard's <h2> nests under this. Receives
          focus on cross-chrome navigation arrival so screen-reader/keyboard users get a landing
          point instead of losing focus context. */}
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        {metadata.name}
      </h1>

      {/* Compact identity header — province name lives in the <h1> above, not repeated here. */}
      <div className="flex items-center gap-3">
        <RaceCrest race={metadata.race} size={40} />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-game-400 truncate">
            {getRaceDisplayName(metadata.race)} · {getPersonalityDisplayName(metadata.personality)} ·{' '}
            {stripEmbeddedLocation(metadata.location.kingdomName, metadata.location.kingdomId)} ({metadata.location.kingdom}:{metadata.location.island})
          </div>
          <div className="text-xs truncate" style={{ color: COLORS.text.tertiary }}>
            {formatCurrentGameDate(sync.currentTick)}
          </div>
        </div>
        <NotificationBellLink unreadCount={unreadCount} />
      </div>

      {/* Live-region: only the diff between renders is announced (Component 3). */}
      <div aria-live="polite" aria-atomic="false" className="sr-only">
        {announcement}
      </div>

      {/* StatusBanner stack — zero or more, dragon → war/ceasefire → protection. */}
      {banners.length > 0 && (
        <div className="flex flex-col gap-2">
          {banners.map((banner) => (
            <StatusBanner key={banner.tone} {...banner} />
          ))}
        </div>
      )}

      {/* Card feed: Recent Activity → Alerts → Military → Armies Away → Active Effects. Land/
          Networth/Gold/Food are now shown in AppShellLayout's sticky header pill instead of a
          Standing/Resources pair here (see AppShellLayout.tsx's SF-138 follow-up note).
          Home is deliberately read-only — no action grid ships here. */}

      {/* SF-123: gains an "Incoming" subsection above the existing, unchanged "Outgoing"
          subsection — see DesktopThroneShell.tsx's matching comment / Decision 10. */}
      <GlanceCard
        title="Recent Activity"
        icon="📜"
      >
        <IncomingActivitySection limit={RECENT_ACTIVITY_CAP_MOBILE} />

        <div>
          <h3
            className="text-xs font-semibold uppercase tracking-wide mb-1"
            style={{ color: COLORS.text.tertiary, letterSpacing: '0.06em' }}
          >
            Outgoing
          </h3>
          {recentActivity.length === 0 ? (
            <div className="text-sm text-game-400">No recent activity.</div>
          ) : (
            recentActivity.map((event, idx) => {
              const { color, message } = describeCombatEvent(event);
              return (
                <div key={idx} className="text-sm" style={{ color }}>
                  {message}
                </div>
              );
            })
          )}
        </div>
      </GlanceCard>

      {alerts.length > 0 && (
        <GlanceCard title="Alerts" icon="⚠️" badge={alerts.length}>
          {alerts.map((alert, idx) => (
            <AlertRow
              key={idx}
              severity={alert.severity}
              message={alert.message}
              actionRoute={alert.actionRoute}
              actionLabel={alert.actionLabel}
            />
          ))}
        </GlanceCard>
      )}

      <GlanceCard title="Military" icon="⚔️">
        <MetricRow label="Offense" value={formatNumber(military.offensePoints)} unit="op" weight="primary" />
        <MetricRow label="Defense" value={formatNumber(military.defensePoints)} unit="dp" weight="primary" />
        <MetricRow label="Generals" value={formatNumber(population.generals)} unit="" weight="tertiary" />
        <MetricRow label="Soldiers" value={formatNumber(population.soldiers)} unit="" weight="tertiary" />
        <MetricRow label={getRaceUnitName(metadata.race, 'defense')} value={formatNumber(population.defenseSpecialists)} unit="" weight="tertiary" />
        <MetricRow label={getRaceUnitName(metadata.race, 'offense')} value={formatNumber(population.offenseSpecialists)} unit="" weight="tertiary" />
        <MetricRow label={getRaceUnitName(metadata.race, 'elite')} value={formatNumber(population.elites)} unit="" weight="tertiary" />
        <MetricRow label="Thieves" value={formatNumber(population.thieves)} unit="" weight="tertiary" />
        <MetricRow label="Wizards" value={formatNumber(population.wizards)} unit="" weight="tertiary" />
      </GlanceCard>

      {military.returningArmies.length > 0 && (
        <GlanceCard title="Armies Away" icon="🚩">
          {military.returningArmies.map((army) => (
            <ArmyRow
              key={army.generalNumber}
              label={`${army.generalNumber}${ordinalSuffix(army.generalNumber)} Army`}
              ticksRemaining={army.ticksRemaining}
              returnTimeFraction={army.returnTimeFraction}
              tickIntervalMs={sync.tickIntervalMs}
              unitsSummary={summarizeArmyUnits(army)}
            />
          ))}
        </GlanceCard>
      )}

      {magic.activeEffects.length > 0 && (
        <GlanceCard title="Active Effects" icon="✨">
          {magic.activeEffects.map((effect, idx) => (
            <EffectRow
              key={idx}
              name={formatEnumLabel(effect.effectType)}
              ticksRemaining={effect.ticksRemaining}
              indefinite={effect.indefinite}
              urgent={effect.ticksRemaining <= 1 && !effect.indefinite}
              beneficial={isEffectBeneficialToOwner(effect.effectType)}
            />
          ))}
        </GlanceCard>
      )}
    </div>
  );
}
